/**
 * Adaptive Project Intent & Site Overview model (#244 #issuecomment-6605969232).
 *
 * Shared across:
 *   - Web & Native-Print PDF (`ProjectIntentOverviewSection.tsx`)
 *   - DOCX export (`reportDocxExport.ts`)
 *   - PPTX export (`reportPptxSections.ts`)
 *
 * Guarantees:
 *   1. For `Recommend an approach` (`screeningMode === 'recommend'`):
 *      - Leads with the saved screening recommendation and its explicit status
 *        (`recommended`, `conditional`, `withheld`, or `unresolved`).
 *      - Presents supported capacity range, power & BTM approach, and cooling &
 *        heat rejection approach, followed by concise rationale, alternatives,
 *        and material evidence gaps + next steps.
 *      - Presents supplied project constraints separately (`recordedBriefRows`).
 *      - When no current recommendation exists, explains what is missing or why
 *        and never turns an input or inference into a recommendation.
 *   2. For `Evaluate my proposal` (`screeningMode === 'evaluate-proposal'`):
 *      - Leads with the user's provided proposal summary (`proposalSummaryLead`).
 *      - Assesses each material dimension against screening evidence as
 *        `supported`, `conditional`, `challenged`, or `unknown`, with the
 *        relevant evidence gap and concrete next step.
 *      - Offers a clearly labeled alternative (`proposalAlternative`) only when
 *        a dimension is conditional/challenged and a source-admitted screening
 *        alternative exists.
 *   3. Preserves provenance citations, material unknowns, user corrections
 *      (`preservedUserDecisions`), legitimate quality/source holds, and flags
 *      stale live inputs against `report.projectIntentAtGen`.
 */

import { deriveIntentForPlanningReport } from './generationImpact/selectedAssessmentIntent';
import {
  derivePresentedScreeningRecommendationSetV1,
  deriveScreeningRecommendationsForPresentation,
  presentScreeningRecommendationFields,
} from './findings/screeningRecommendationsForPresentation';
import { isScreeningRecommendationMode } from './intent/screeningIntentMode';
import {
  TIMELINE_BUCKET_LABEL,
  effectivePhases,
  effectivePower,
  formatInServiceDate,
} from './projectIntent';
import { effectiveWorkloadProfile, getWorkloadProfile } from './workloadProfile';
import type { ProjectIntent, RagCategory, RagStatus, SiteRagReport } from '../types/siteRag';
import type { SiteEntity } from '../types/site';
import type { DatasheetRow } from '../components/siterag/report/editorial';

export type LooseOverviewReport = SiteRagReport & {
  scores?: Array<{ category: string; status?: RagStatus; detail?: string; headline?: string }>;
  gridAnalysis?: {
    availableCapacity?: string;
    nearestSubstation?: string;
    interconnectionTimeline?: string;
  };
  coolingAnalysis?: {
    recommendedSystem?: string;
    waterAvailability?: string;
  };
  btmViability?: {
    recommendedPath?: string;
    summary?: string;
  };
};

export function titleizeEnum(value: string | undefined | null): string | null {
  if (!value || value === 'undecided' || value === 'undefined') return null;
  return value
    .split(/[-_\s]+/)
    .map(w => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

export function hasDeclaredProposalFields(intent: ProjectIntent | undefined): boolean {
  if (!intent) return false;
  const power = effectivePower(intent);
  if (power.bucket !== 'undecided') return true;
  if (titleizeEnum(intent.coolingStrategy) != null) return true;
  if (
    intent.nuclearInterest === 'primary' ||
    intent.nuclearInterest === 'considering' ||
    intent.nuclearInterest === 'no'
  ) {
    return true;
  }
  const phased = effectivePhases(intent);
  if (phased.isPhased && phased.phaseCount > 1) return true;
  if (phased.initialInServiceDate || intent.inServiceDate) return true;
  return false;
}

export function resolveReportScreeningMode(
  report: SiteRagReport,
  intent?: ProjectIntent,
): 'recommend' | 'evaluate-proposal' {
  const effectiveIntent = intent ?? deriveIntentForPlanningReport(report, report.projectIntentAtGen);
  if (isScreeningRecommendationMode(effectiveIntent)) return 'recommend';
  if (effectiveIntent?.screeningMode === 'evaluate-proposal') return 'evaluate-proposal';
  if (hasDeclaredProposalFields(effectiveIntent)) return 'evaluate-proposal';
  return 'recommend';
}

export function resolveIntentSpecRail(report: SiteRagReport): DatasheetRow[] {
  const items: DatasheetRow[] = [];
  const intent = deriveIntentForPlanningReport(report, report.projectIntentAtGen);
  const recs = deriveScreeningRecommendationsForPresentation(report, report.siteId);
  if (intent) {
    if (intent.screeningMode === 'recommend') {
      items.push({ key: 'Screening Mode', value: 'Recommend an approach' });
    } else if (intent.screeningMode === 'evaluate-proposal') {
      items.push({ key: 'Screening Mode', value: 'Evaluate my proposal' });
    }
    const phased = effectivePhases(intent);
    const power = effectivePower(intent);
    const phasedUltimateTbdWithFloor =
      phased.isPhased &&
      phased.totalCapacityMW == null &&
      phased.ultimateCapacityBucket == null &&
      phased.knownCapacityFloorMW != null;
    if (phasedUltimateTbdWithFloor) {
      items.push({
        key: 'Target Power',
        value: `Ultimate TBD (${phased.knownCapacityFloorMW} MW known floor)`,
      });
    } else if (power.bucket !== 'undecided') {
      items.push({ key: 'Target Power', value: power.displayLabel });
    } else if (intent.screeningMode === 'recommend' && recs['dc-power']?.value) {
      items.push({ key: 'Recommended Load Range', value: recs['dc-power'].value });
    }
    const profile = effectiveWorkloadProfile(intent);
    const profileLabel = getWorkloadProfile(profile).label;
    if (profileLabel) {
      items.push({ key: 'Workload', value: profileLabel });
    }
    const cooling = titleizeEnum(intent.coolingStrategy);
    if (cooling) {
      items.push({ key: 'Cooling', value: cooling });
    } else if (intent.screeningMode === 'recommend' && recs['cooling-strategy']?.value) {
      items.push({ key: 'Recommended Cooling', value: recs['cooling-strategy'].value });
    }
    if (intent.screeningMode === 'recommend' && recs['btm-architecture']?.value) {
      items.push({ key: 'Recommended Power Path', value: recs['btm-architecture'].value });
    }
    if (intent.nuclearInterest === 'primary' || intent.nuclearInterest === 'considering') {
      items.push({
        key: 'Nuclear Interest',
        value: intent.nuclearInterest === 'primary' ? 'Primary path' : 'Under consideration',
      });
    } else if (intent.nuclearInterest === 'no') {
      items.push({
        key: 'Nuclear Interest',
        value: 'Excluded',
      });
    }
    if (phased.isPhased && phased.phaseCount > 1) {
      items.push({ key: 'Phasing', value: `${phased.phaseCount} phases` });
    }
    const cod = phased.initialInServiceDate ?? intent.inServiceDate ?? null;
    if (cod) {
      if (
        phased.isPhased &&
        phased.phaseCount > 1 &&
        !phased.ultimateInServiceDate &&
        phased.ultimateCodTimeline
      ) {
        const finalPhaseLabel =
          phased.phases[phased.phases.length - 1]?.label || `Phase ${phased.phaseCount}`;
        items.push({ key: 'Phase 1 COD', value: formatInServiceDate(cod) });
        items.push({
          key: `${finalPhaseLabel} Target`,
          value: `${TIMELINE_BUCKET_LABEL[phased.ultimateCodTimeline]} (planning target, not a COD)`,
        });
      } else if (phased.isPhased && phased.phaseCount > 1 && !phased.ultimateInServiceDate) {
        const finalPhaseLabel =
          phased.phases[phased.phases.length - 1]?.label || `Phase ${phased.phaseCount}`;
        items.push({ key: 'Phase 1 COD', value: formatInServiceDate(cod) });
        items.push({
          key: `${finalPhaseLabel} COD`,
          value: 'TBD',
        });
      } else {
        items.push({ key: 'First COD', value: formatInServiceDate(cod) });
      }
    }
  }
  const acres = report.siteBoundaryAggregate?.totalArea ?? report.siteArea ?? null;
  if (typeof acres === 'number' && Number.isFinite(acres) && acres > 0) {
    items.push({
      key: 'Site Area',
      value: `${acres.toLocaleString('en-US', { maximumFractionDigits: 0 })} acres`,
    });
  }
  return items;
}

function getReportCategoryList(report: SiteRagReport): Array<
  RagCategory & {
    category?: string;
    name?: string;
    summary?: string;
    metrics?: Array<{
      id?: string;
      name?: string;
      label?: string;
      value?: string | number | null;
      source?: string;
      missing?: boolean;
    }>;
  }
> {
  return (report.categories ??
    (report.conclusion as { categories?: unknown })?.categories ??
    []) as never;
}

function findCategoryScore(
  report: SiteRagReport,
  pattern: RegExp,
): { status?: RagStatus | 'withheld'; detail?: string } | undefined {
  const loose = report as LooseOverviewReport;
  const scoreMatch = (loose.scores ?? []).find(s => pattern.test(s.category));
  if (scoreMatch) {
    return { status: scoreMatch.status, detail: scoreMatch.detail || scoreMatch.headline };
  }
  const catMatch = getReportCategoryList(report).find(c =>
    pattern.test(c.category ?? c.name ?? c.label ?? c.id ?? ''),
  );
  if (catMatch) {
    const looseCat = catMatch as Omit<RagCategory, 'status'> & {
      category?: string;
      summary?: string;
      status?: RagStatus | 'withheld';
    };
    return {
      status: looseCat.status,
      detail: looseCat.summary ?? catMatch.explanation,
    };
  }
  return undefined;
}

export type ProposalDimensionStatus = 'supported' | 'conditional' | 'challenged' | 'unknown';

export function alignmentBadgeFromAssessmentStatus(
  status: ProposalDimensionStatus,
): { status: ProposalDimensionStatus; label: string; className: string } {
  if (status === 'supported') {
    return {
      status: 'supported',
      label: 'SUPPORTED',
      className: 'bg-emerald-500/15 text-emerald-800 border-emerald-500/30',
    };
  }
  if (status === 'challenged') {
    return {
      status: 'challenged',
      label: 'CHALLENGED / HOLD',
      className: 'bg-rose-500/15 text-rose-800 border-rose-500/30',
    };
  }
  if (status === 'conditional') {
    return {
      status: 'conditional',
      label: 'CONDITIONAL',
      className: 'bg-amber-500/15 text-amber-800 border-amber-500/30',
    };
  }
  return {
    status: 'unknown',
    label: 'UNKNOWN / UNVERIFIED',
    className: 'bg-slate-500/10 text-slate-700 border-slate-500/25',
  };
}

function mapRagStatusToProposalStatus(
  status: RagStatus | 'withheld' | undefined,
  options?: { forceConditionalWhenGreen?: boolean; hasVerifiedDetail?: boolean },
): ProposalDimensionStatus {
  if (status === 'red') return 'challenged';
  if (status === 'amber') return 'conditional';
  if (status === 'green') {
    return options?.forceConditionalWhenGreen ? 'conditional' : 'supported';
  }
  if (status === 'withheld') return 'unknown';
  return options?.hasVerifiedDetail ? 'conditional' : 'unknown';
}

export interface ProposalAlignmentRow {
  dimension: string;
  declaredProposal: string;
  verifiedEvidence: string;
  gapSummary: string;
  nextStep: string;
  gapAndNextStep: string;
  assessmentStatus: ProposalDimensionStatus;
  badge: { status: ProposalDimensionStatus; label: string; className: string };
}

function admitEvidenceField(
  report: SiteRagReport,
  text: string | undefined | null,
): { text: string | null; withheldBySource: boolean } {
  if (!text || !text.trim()) return { text: null, withheldBySource: false };
  if (report.qualityHold === 'needs-revision' || (report.qualityHoldFindings?.length ?? 0) > 0) {
    return { text: null, withheldBySource: true };
  }
  const admitted = presentScreeningRecommendationFields(report, [text]);
  if (!admitted || !admitted[0]?.trim()) {
    return { text: null, withheldBySource: true };
  }
  return { text: admitted[0], withheldBySource: false };
}

export function buildProposalAlignmentRows(
  report: SiteRagReport,
  intent: ProjectIntent | undefined,
): ProposalAlignmentRow[] {
  const loose = report as LooseOverviewReport;
  const rows: ProposalAlignmentRow[] = [];
  const power = intent
    ? effectivePower(intent)
    : { bucket: 'undecided' as const, displayLabel: 'Undecided' };
  const powerScore = findCategoryScore(report, /power|grid|energy/i);
  const hasExplicitMw =
    typeof intent?.powerRequirementMW === 'number' && Number.isFinite(intent.powerRequirementMW);
  const rawBasis = intent?.powerBasis as string | undefined;
  const basisLabel =
    rawBasis === 'it-load'
      ? hasExplicitMw
        ? 'Recorded IT load input'
        : 'IT basis; exact MW unconfirmed'
      : rawBasis === 'facility' || rawBasis === 'total-facility'
        ? hasExplicitMw
          ? 'Recorded facility demand input'
          : 'Default/preliminary facility basis; confirmation pending'
        : 'Basis unconfirmed; confirmation pending';

  const phased = intent
    ? effectivePhases(intent)
    : {
        isPhased: false,
        phaseCount: 0,
        phases: [],
        totalCapacityMW: null,
        ultimateCapacityBucket: null,
        knownCapacityFloorMW: null,
        initialInServiceDate: null,
        ultimateInServiceDate: null,
        ultimateCodTimeline: null,
        lastKnownInServiceDate: null,
      };
  const phasedUltimateTbdWithFloor =
    phased.isPhased &&
    phased.totalCapacityMW == null &&
    phased.ultimateCapacityBucket == null &&
    phased.knownCapacityFloorMW != null;

  if (power.bucket !== 'undecided' || phasedUltimateTbdWithFloor || loose.gridAnalysis) {
    const capAdmitted = admitEvidenceField(
      report,
      loose.gridAnalysis?.availableCapacity
        ? `Capacity: ${loose.gridAnalysis.availableCapacity}`
        : null,
    );
    const poiAdmitted = admitEvidenceField(
      report,
      loose.gridAnalysis?.nearestSubstation
        ? `POI: ${loose.gridAnalysis.nearestSubstation}`
        : null,
    );
    const scoreDetailAdmitted = admitEvidenceField(report, powerScore?.detail);
    const anyPowerHeld =
      capAdmitted.withheldBySource ||
      poiAdmitted.withheldBySource ||
      scoreDetailAdmitted.withheldBySource;
    const gridEvidenceParts = [
      capAdmitted.text,
      poiAdmitted.text,
      scoreDetailAdmitted.text,
    ].filter((v): v is string => Boolean(v));
    const gridCat = getReportCategoryList(report).find(c =>
      /power|grid|energy/i.test(c.category ?? c.name ?? c.label ?? c.id ?? ''),
    );
    const isPlanningDefaultOnlyGrid = Boolean(
      gridCat?.metrics?.some(m => {
        const looseMetric = m as {
          source?: string;
          name?: string;
          label?: string;
          value?: string;
        };
        return (
          looseMetric.source === 'planning-default' ||
          /planning-default(?:\s+elcc)?/i.test(
            `${looseMetric.name ?? looseMetric.label ?? ''} ${looseMetric.value ?? ''}`,
          )
        );
      }) || /planning-default(?:\s+elcc)?/i.test(scoreDetailAdmitted.text ?? ''),
    );
    const rawPowerStatus = powerScore?.status ?? report.overallStatus;
    const assessmentStatus: ProposalDimensionStatus =
      anyPowerHeld && gridEvidenceParts.length === 0
        ? 'unknown'
        : mapRagStatusToProposalStatus(rawPowerStatus, {
            forceConditionalWhenGreen: isPlanningDefaultOnlyGrid || gridEvidenceParts.length === 0,
            hasVerifiedDetail: gridEvidenceParts.length > 0,
          });
    const baseGridEvidence =
      anyPowerHeld && gridEvidenceParts.length === 0
        ? 'Evidence withheld — cited source is unavailable or under quality hold.'
        : gridEvidenceParts[0] ?? 'Subject to utility capacity & POI verification';
    const verifiedGridEvidence =
      isPlanningDefaultOnlyGrid && !/utility headroom/i.test(baseGridEvidence)
        ? `${baseGridEvidence} (planning-default ELCC model — utility headroom & station voltage unverified)`
        : baseGridEvidence;
    const gapSummary =
      anyPowerHeld && gridEvidenceParts.length === 0
        ? 'Gap: Supporting utility/grid source evidence is withheld under source or quality hold.'
        : isPlanningDefaultOnlyGrid
          ? 'Gap: Utility feeder/substation headroom and delivery voltage are unverified (planning-default model only).'
          : assessmentStatus === 'challenged'
            ? 'Gap: Declared capacity exceeds current screened utility/site power envelope.'
            : gridEvidenceParts.length === 0
              ? 'Gap: No utility capacity allocation or POI study on file.'
              : 'Gap: Modeled screening capacity is not an executed utility allocation.';
    const nextStep = isPlanningDefaultOnlyGrid
      ? 'Request utility load-flow / System Impact Study and confirm IT vs. total-facility MW basis.'
      : assessmentStatus === 'challenged'
        ? 'Evaluate phased ramp, BTM bridge generation, or transmission upgrade study.'
        : gridEvidenceParts.length === 0
          ? 'Submit utility service inquiry and obtain substation capacity confirmation.'
          : 'Complete formal utility interconnection study.';
    const gapAndNextStep = `${gapSummary} Next step: ${nextStep}`;
    rows.push({
      dimension: 'Target Capacity & Basis',
      declaredProposal: phasedUltimateTbdWithFloor
        ? `Ultimate capacity TBD (${phased.knownCapacityFloorMW} MW known floor; ${basisLabel})`
        : power.bucket !== 'undecided'
          ? `${power.displayLabel} (${basisLabel})`
          : 'Open / Not specified',
      verifiedEvidence: verifiedGridEvidence,
      gapSummary,
      nextStep,
      gapAndNextStep,
      assessmentStatus,
      badge: alignmentBadgeFromAssessmentStatus(assessmentStatus),
    });
  }

  const cod = phased.initialInServiceDate ?? intent?.inServiceDate ?? null;
  const timelineLabel = titleizeEnum(intent?.timeline);
  const pathwayTimeframes = (report.developmentPathway ?? [])
    .map(p => {
      const lp = p as { phase?: string; timeframe?: string; cod?: string };
      const tf = lp.timeframe ?? lp.cod;
      if (!tf) return null;
      return lp.phase && (report.developmentPathway?.length ?? 0) > 1
        ? `${lp.phase}: ${tf}`
        : tf;
    })
    .filter((v): v is string => Boolean(v));
  const pathwayTimeframe =
    pathwayTimeframes.length > 0 ? pathwayTimeframes.join(' · ') : undefined;
  if (
    (phased.isPhased && phased.phaseCount > 1) ||
    cod ||
    timelineLabel ||
    loose.gridAnalysis?.interconnectionTimeline
  ) {
    const finalPhaseLabel =
      phased.phases[phased.phases.length - 1]?.label || `Phase ${phased.phaseCount}`;
    const schedulePart = cod
      ? phased.isPhased &&
        phased.phaseCount > 1 &&
        !phased.ultimateInServiceDate &&
        phased.ultimateCodTimeline
        ? `Phase 1 COD ${formatInServiceDate(cod)} · ${finalPhaseLabel}: ${TIMELINE_BUCKET_LABEL[phased.ultimateCodTimeline]} (planning target, not a COD)`
        : phased.isPhased && phased.phaseCount > 1 && !phased.ultimateInServiceDate
          ? `Phase 1 COD ${formatInServiceDate(cod)} · ${finalPhaseLabel} COD TBD`
          : `First COD ${formatInServiceDate(cod)}`
      : timelineLabel;
    const declaredParts = [
      phased.isPhased && phased.phaseCount > 1
        ? `${phased.phaseCount} phases (requested schedule; basis confirmation pending)`
        : null,
      schedulePart,
    ].filter(Boolean);
    const ixTimelineAdmitted = admitEvidenceField(report, loose.gridAnalysis?.interconnectionTimeline);
    const pathwayTfAdmitted = admitEvidenceField(report, pathwayTimeframe);
    const scheduleScore = findCategoryScore(report, /schedule|timeline|phasing/i);
    const scheduleScoreAdmitted = admitEvidenceField(report, scheduleScore?.detail);
    const anyScheduleHeld = ixTimelineAdmitted.withheldBySource || pathwayTfAdmitted.withheldBySource ||
      scheduleScoreAdmitted.withheldBySource;
    const scheduleEvidenceText = scheduleScoreAdmitted.text ?? ixTimelineAdmitted.text ?? pathwayTfAdmitted.text;
    const hasScheduleEvidence = Boolean(scheduleEvidenceText);
    // A grid capacity score does not establish whether a delivery date is supported.
    // Only an admitted schedule assessment may supply a schedule status.
    const assessmentStatus: ProposalDimensionStatus =
      !hasScheduleEvidence
        ? 'unknown'
        : mapRagStatusToProposalStatus(scheduleScoreAdmitted.text ? scheduleScore?.status : undefined, {
            hasVerifiedDetail: hasScheduleEvidence,
          });
    const scheduleGap =
      anyScheduleHeld && !hasScheduleEvidence
        ? 'Gap: Supporting interconnection schedule source evidence is withheld under source or quality hold.'
        : hasScheduleEvidence
          ? 'Gap: Screening timeline remains subject to utility queue position and equipment lead times.'
          : 'Gap: Utility energization schedule and queue position are unconfirmed.';
    const scheduleNextStep = hasScheduleEvidence
      ? 'Lock interconnection study milestones and phase energization schedule.'
      : 'Obtain utility interconnection queue schedule for each phase.';
    rows.push({
      dimension: 'Delivery Schedule & Phasing',
      declaredProposal: declaredParts.join(' · ') || 'Flexible timeline',
      verifiedEvidence:
        anyScheduleHeld && !hasScheduleEvidence
          ? 'Evidence withheld — cited source is unavailable or under quality hold.'
          : scheduleEvidenceText ||
            'Subject to utility interconnection queue & study schedule',
      gapSummary: scheduleGap,
      nextStep: scheduleNextStep,
      gapAndNextStep: `${scheduleGap} Next step: ${scheduleNextStep}`,
      assessmentStatus,
      badge: alignmentBadgeFromAssessmentStatus(assessmentStatus),
    });
  }

  const coolingLabel = titleizeEnum(intent?.coolingStrategy);
  const heatRejectionLabel = titleizeEnum(intent?.bopHeatRejection);
  const coolingScore = findCategoryScore(report, /cooling|water|climate|thermal/i);
  if (coolingLabel || heatRejectionLabel || loose.coolingAnalysis) {
    const recSysAdmitted = admitEvidenceField(
      report,
      loose.coolingAnalysis?.recommendedSystem
        ? `Screen: ${loose.coolingAnalysis.recommendedSystem}`
        : null,
    );
    const waterAvailAdmitted = admitEvidenceField(
      report,
      loose.coolingAnalysis?.waterAvailability
        ? `Water: ${loose.coolingAnalysis.waterAvailability}`
        : null,
    );
    const coolScoreAdmitted = admitEvidenceField(report, coolingScore?.detail);
    const anyCoolHeld =
      recSysAdmitted.withheldBySource ||
      waterAvailAdmitted.withheldBySource ||
      coolScoreAdmitted.withheldBySource;
    const coolingEvidenceParts = [
      recSysAdmitted.text,
      waterAvailAdmitted.text,
      coolScoreAdmitted.text,
    ].filter((v): v is string => Boolean(v));
    const prelimCooling = [coolingLabel, heatRejectionLabel].filter(Boolean).join(' · ');
    const assessmentStatus: ProposalDimensionStatus =
      anyCoolHeld && coolingEvidenceParts.length === 0
        ? 'unknown'
        : mapRagStatusToProposalStatus(coolingScore?.status, {
            forceConditionalWhenGreen: coolingEvidenceParts.length === 0,
            hasVerifiedDetail: coolingEvidenceParts.length > 0,
          });
    const coolingGap =
      anyCoolHeld && coolingEvidenceParts.length === 0
        ? 'Gap: Supporting cooling/water source evidence is withheld under source or quality hold.'
        : coolingEvidenceParts.length > 0
          ? 'Gap: Preliminary cooling input requires site ASHRAE wet-bulb and utility water allocation verification.'
          : 'Gap: Site cooling water availability and ambient derating envelope are unverified.';
    const coolingNextStep =
      coolingEvidenceParts.length > 0
        ? 'Confirm peak thermal rejection duty and municipal water service letter.'
        : 'Complete local meteorological and water-supply capacity check.';
    rows.push({
      dimension: 'Cooling & Heat Rejection',
      declaredProposal: prelimCooling
        ? `${prelimCooling} (recorded preliminary input; confirmation pending)`
        : 'Open cooling strategy',
      verifiedEvidence:
        anyCoolHeld && coolingEvidenceParts.length === 0
          ? 'Evidence withheld — cited source is unavailable or under quality hold.'
          : coolingEvidenceParts[0] ?? 'Subject to site design wet-bulb & water supply verification',
      gapSummary: coolingGap,
      nextStep: coolingNextStep,
      gapAndNextStep: `${coolingGap} Next step: ${coolingNextStep}`,
      assessmentStatus,
      badge: alignmentBadgeFromAssessmentStatus(assessmentStatus),
    });
  }

  const nuclearStance = intent?.nuclearInterest;
  if (nuclearStance && nuclearStance !== 'undecided') {
    const proposalLabel =
      nuclearStance === 'no'
        ? 'Nuclear / SMR excluded'
        : nuclearStance === 'primary'
          ? 'Nuclear as primary pathway (preliminary input)'
          : 'Nuclear under consideration (preliminary input)';
    const btmSummaryAdmitted = admitEvidenceField(report, loose.btmViability?.summary);
    const generationScore = findCategoryScore(report, /btm|nuclear|on[ -]?site generation/i);
    const generationScoreAdmitted = admitEvidenceField(report, generationScore?.detail);
    const generationEvidence = generationScoreAdmitted.text ?? btmSummaryAdmitted.text;
    const hasBtmEvidence = Boolean(generationEvidence);
    const generationEvidenceHeld = btmSummaryAdmitted.withheldBySource || generationScoreAdmitted.withheldBySource;
    // Preserve the user's nuclear posture as an input. Neither that choice nor
    // an unrelated grid score proves feasibility of the generation dimension.
    const assessmentStatus: ProposalDimensionStatus =
      !hasBtmEvidence
        ? 'unknown'
        : mapRagStatusToProposalStatus(generationScoreAdmitted.text ? generationScore?.status : undefined, {
            hasVerifiedDetail: hasBtmEvidence,
          });
    const nuclearGap =
      generationEvidenceHeld && !hasBtmEvidence
        ? 'Gap: Supporting BTM generation source evidence is withheld under source or quality hold.'
        : nuclearStance === 'no'
          ? 'Gap: The recorded nuclear exclusion does not establish feasibility of the selected non-nuclear supply architecture.'
          : 'Gap: Nuclear/SMR siting exclusion zones, cooling water, and NRC licensing horizon require Stage 2 diligence.';
    const nuclearNextStep =
      nuclearStance === 'no'
        ? 'Verify the selected non-nuclear supply architecture and any applicable fuel and air-permitting requirements.'
        : 'Screen parcel setback and seismic/hydrologic suitability.';
    rows.push({
      dimension: 'On-Site Generation & Nuclear Posture',
      declaredProposal: proposalLabel,
      verifiedEvidence:
        generationEvidenceHeld && !hasBtmEvidence
          ? 'Evidence withheld — cited source is unavailable or under quality hold.'
          : generationEvidence ||
            (nuclearStance === 'no'
              ? 'Nuclear / SMR exclusion is a recorded brief constraint; generation feasibility evidence is not available.'
              : 'Site-specific BTM and licensing feasibility evidence is not available.'),
      gapSummary: nuclearGap,
      nextStep: nuclearNextStep,
      gapAndNextStep: `${nuclearGap} Next step: ${nuclearNextStep}`,
      assessmentStatus,
      badge: alignmentBadgeFromAssessmentStatus(assessmentStatus),
    });
  }

  const acres = report.siteBoundaryAggregate?.totalArea ?? report.siteArea ?? null;
  const landScore = findCategoryScore(report, /land|site|environmental|flood|acre/i);
  if ((typeof acres === 'number' && Number.isFinite(acres) && acres > 0) || landScore) {
    const landDetailAdmitted = admitEvidenceField(report, landScore?.detail);
    const landCat = getReportCategoryList(report).find(c =>
      /land|site|environmental|flood|acre/i.test(c.category ?? c.name ?? c.label ?? c.id ?? ''),
    );
    const hasMissingLandMetrics = Boolean(
      landCat?.metrics?.some(
        m =>
          m.missing ||
          /unassessed|unknown|not specified|evidence gap/i.test(String(m.value ?? '')),
      ) || /evidence gap\s*[—-]\s*excluded from scoring|not specified/i.test(landDetailAdmitted.text ?? ''),
    );
    const assessmentStatus: ProposalDimensionStatus =
      landDetailAdmitted.withheldBySource && !landDetailAdmitted.text
        ? 'unknown'
        : mapRagStatusToProposalStatus(landScore?.status, {
            forceConditionalWhenGreen: hasMissingLandMetrics,
            hasVerifiedDetail: Boolean(landDetailAdmitted.text || (typeof acres === 'number' && acres > 0)),
          });
    const baseLandEvidence =
      landDetailAdmitted.withheldBySource && !landDetailAdmitted.text
        ? 'Evidence withheld — cited source is unavailable or under quality hold.'
        : landDetailAdmitted.text ||
          (typeof acres === 'number' && acres > 0
            ? `${acres.toLocaleString('en-US', { maximumFractionDigits: 2 })} gross acres screened; usable/buildable acreage requires flood/wetland/setback exclusions`
            : 'Buildable net acreage subject to parcel exclusion mapping');
    const verifiedLandEvidence =
      hasMissingLandMetrics && !/net buildable area & flood exposure unassessed/i.test(baseLandEvidence)
        ? `${baseLandEvidence} (net buildable area & flood exposure unassessed)`
        : baseLandEvidence;
    const landGap =
      landDetailAdmitted.withheldBySource && !landDetailAdmitted.text
        ? 'Gap: Supporting land/environmental source evidence is withheld under source or quality hold.'
        : hasMissingLandMetrics
          ? 'Gap: Net buildable area, FEMA floodplain, and wetland exclusions are unassessed.'
          : 'Gap: Gross acreage is not confirmed net buildable area.';
    const landNextStep = hasMissingLandMetrics
      ? 'Complete ALTA/topographic survey, wetland delineation, and floodplain overlay.'
      : 'Verify municipal setbacks, easements, and grading envelope in test-fit.';
    rows.push({
      dimension: 'Site Envelope & Buildable Land',
      declaredProposal:
        typeof acres === 'number' && acres > 0
          ? `${acres.toLocaleString('en-US', { maximumFractionDigits: 2 })} gross acres selected study area (not net buildable)`
          : 'Study area boundary',
      verifiedEvidence: verifiedLandEvidence,
      gapSummary: landGap,
      nextStep: landNextStep,
      gapAndNextStep: `${landGap} Next step: ${landNextStep}`,
      assessmentStatus,
      badge: alignmentBadgeFromAssessmentStatus(assessmentStatus),
    });
  }

  return rows;
}

export interface StaleIntentDriftInfo {
  genMode?: 'recommend' | 'evaluate-proposal';
  liveMode?: 'recommend' | 'evaluate-proposal';
  hasModeDrift: boolean;
  hasInputDrift: boolean;
  reportModeLabel: string;
  liveModeLabel: string;
  changedFields: string[];
  summaryText: string;
}

export function detectProjectIntentDrift(
  report: SiteRagReport,
  reportSite?: SiteEntity,
): StaleIntentDriftInfo {
  const genIntent = report.projectIntentAtGen;
  const liveIntent = reportSite?.projectIntent;
  const genMode = genIntent?.screeningMode;
  const liveMode = liveIntent?.screeningMode;
  const reportModeLabel =
    genMode === 'evaluate-proposal'
      ? 'Evaluate my proposal (evaluate-proposal)'
      : 'Recommend an approach (recommend)';
  const liveModeLabel =
    liveMode === 'evaluate-proposal'
      ? 'Evaluate my proposal (evaluate-proposal)'
      : 'Recommend an approach (recommend)';

  if (!genIntent || !liveIntent) {
    return {
      genMode,
      liveMode,
      hasModeDrift: false,
      hasInputDrift: false,
      reportModeLabel,
      liveModeLabel,
      changedFields: [],
      summaryText: '',
    };
  }

  const hasModeDrift = Boolean(
    genMode &&
      liveMode &&
      genMode !== liveMode,
  );

  const changedFields: string[] = [];
  if (hasModeDrift) {
    changedFields.push(`Screening mode (${reportModeLabel} → ${liveModeLabel})`);
  }
  if (
    (genIntent.powerRequirement ?? 'undecided') !== (liveIntent.powerRequirement ?? 'undecided') ||
    (genIntent.powerRequirementMW ?? null) !== (liveIntent.powerRequirementMW ?? null)
  ) {
    const genPower = effectivePower(genIntent).displayLabel;
    const livePower = effectivePower(liveIntent).displayLabel;
    if (genPower !== livePower) {
      changedFields.push(`Target power (${genPower} → ${livePower})`);
    }
  }
  if ((genIntent.coolingStrategy ?? 'undecided') !== (liveIntent.coolingStrategy ?? 'undecided')) {
    changedFields.push(
      `Cooling (${titleizeEnum(genIntent.coolingStrategy) ?? 'Undecided'} → ${titleizeEnum(liveIntent.coolingStrategy) ?? 'Undecided'})`,
    );
  }
  if ((genIntent.nuclearInterest ?? 'undecided') !== (liveIntent.nuclearInterest ?? 'undecided')) {
    changedFields.push(
      `Nuclear posture (${genIntent.nuclearInterest ?? 'undecided'} → ${liveIntent.nuclearInterest ?? 'undecided'})`,
    );
  }
  if ((genIntent.inServiceDate ?? null) !== (liveIntent.inServiceDate ?? null)) {
    changedFields.push(
      `Target COD (${genIntent.inServiceDate ?? 'none'} → ${liveIntent.inServiceDate ?? 'none'})`,
    );
  }

  return {
    genMode,
    liveMode,
    hasModeDrift,
    hasInputDrift: changedFields.length > 0,
    reportModeLabel,
    liveModeLabel,
    changedFields,
    summaryText:
      changedFields.length > 0
        ? `Live Project Intent differs from this frozen report snapshot (${changedFields.join('; ')}).`
        : '',
  };
}

export interface AdaptiveOverviewSummaryItem {
  id: string;
  label: string;
  value: string;
  statusLabel: string;
  notes: string[];
}

export interface AdaptiveOverviewGapItem {
  id: string;
  label: string;
  gap: string;
  nextStep?: string;
}

export interface AdaptiveProjectIntentOverviewModel {
  screeningMode: 'recommend' | 'evaluate-proposal';
  showModeScorecard: boolean;
  specRailItems: DatasheetRow[];
  recordedBriefRows: Array<DatasheetRow & { label: string }>;
  recommendationStatus: {
    state: 'recommended' | 'conditional' | 'withheld' | 'unresolved';
    status: 'recommended' | 'conditional' | 'withheld' | 'unresolved';
    label: string;
    explanation: string;
  };
  powerRecommendation: AdaptiveOverviewSummaryItem;
  supportingRecommendations: AdaptiveOverviewSummaryItem[];
  rationaleRows: AdaptiveOverviewSummaryItem[];
  alternativesList: string[];
  evidenceGaps: AdaptiveOverviewGapItem[];
  proposalSummaryLead: string;
  proposalRows: ProposalAlignmentRow[];
  proposalAlternative: { label: string; summary: string; rationale?: string } | null;
  preservedUserDecisions: string[];
  intentDrift: StaleIntentDriftInfo;
}

const distinctNotes = (values: Array<string | undefined>): string[] => [
  ...new Set(values.filter((value): value is string => Boolean(value?.trim()))),
];

function questionStatusBadgeLabel(status: string | undefined, hasValue: boolean): string {
  if (status === 'recommended') return 'RECOMMENDED';
  if (status === 'alternatives') return 'ALTERNATIVES';
  if (status === 'withheld-source') return 'WITHHELD (SOURCE UNAVAILABLE)';
  if (status === 'unresolved') return 'UNRESOLVED';
  return hasValue ? 'SCREENED (LEGACY)' : 'NOT PRODUCED';
}

export function buildAdaptiveProjectIntentOverviewModel(
  report: SiteRagReport,
  reportSite?: SiteEntity,
): AdaptiveProjectIntentOverviewModel {
  const looseReport = report as LooseOverviewReport;
  const specRailItems = resolveIntentSpecRail(report);
  const effectiveIntent = deriveIntentForPlanningReport(report, report.projectIntentAtGen);
  const screeningMode = resolveReportScreeningMode(report, effectiveIntent);
  const savedRecs = deriveScreeningRecommendationsForPresentation(report, report.siteId);
  const savedSet = derivePresentedScreeningRecommendationSetV1(report, report.siteId);
  const dcPowerDiag = savedSet.questions['dc-power'];
  const btmArchDiag = savedSet.questions['btm-architecture'];
  const powerTypeDiag = savedSet.questions['power-type'];
  const coolingStratDiag = savedSet.questions['cooling-strategy'];
  const coolingTypeDiag = savedSet.questions['cooling-type'];
  const hasSavedRecs = Object.keys(savedRecs).length > 0;

  const isSourceReady = savedSet.sourceState === 'ready';
  const legacyCapacity = isSourceReady
    ? presentScreeningRecommendationFields(report, [
        looseReport.gridAnalysis?.availableCapacity ?? '',
      ])?.[0]
    : undefined;
  const legacyPower = isSourceReady
    ? presentScreeningRecommendationFields(report, [
        looseReport.btmViability?.recommendedPath ?? '',
        looseReport.btmViability?.summary ?? '',
      ])
    : null;
  const legacyCooling = isSourceReady
    ? presentScreeningRecommendationFields(report, [
        looseReport.coolingAnalysis?.recommendedSystem ?? '',
        looseReport.coolingAnalysis?.waterAvailability ?? '',
      ])
    : null;

  const capValue =
    savedRecs['dc-power']?.value ??
    (legacyCapacity
      ? `Grid POI evidence: ${legacyCapacity} (sizing range pending study)`
      : 'Pending utility & buildable-envelope confirmation');
  const powerValue =
    distinctNotes([savedRecs['btm-architecture']?.value, savedRecs['power-type']?.value]).join(
      '\n\n',
    ) ||
    legacyPower?.[0] ||
    'No saved power recommendation is available';
  const coolingValue =
    distinctNotes([savedRecs['cooling-strategy']?.value, savedRecs['cooling-type']?.value]).join(
      ' · ',
    ) ||
    legacyCooling?.[0] ||
    'No saved cooling recommendation is available';

  const stripRepeatedLongValueFromNote = (
    note: string | undefined,
    displayedValues: ReadonlyArray<string | undefined>,
  ): string | undefined => {
    if (!note) return undefined;
    let cleaned = note.trim();
    const advisoryPrefix = 'Saved screening recommendation; review before adopting.';
    for (const rawVal of displayedValues) {
      const val = rawVal?.trim();
      if (!val || val.length < 200) continue;
      if (cleaned.startsWith(advisoryPrefix)) {
        const afterAdvisory = cleaned.slice(advisoryPrefix.length).trim();
        if (afterAdvisory.startsWith(val)) {
          const remainder = afterAdvisory.slice(val.length).trim();
          cleaned = remainder ? `${advisoryPrefix} ${remainder}` : '';
        }
      } else if (cleaned.startsWith(val)) {
        cleaned = cleaned.slice(val.length).trim();
      }
    }
    return cleaned || undefined;
  };

  const recommendationRows: AdaptiveOverviewSummaryItem[] = [
    {
      id: 'capacity',
      label: 'Supportable capacity range',
      value: capValue,
      statusLabel: questionStatusBadgeLabel(
        dcPowerDiag.status,
        Boolean(savedRecs['dc-power']?.value || legacyCapacity),
      ),
      notes: distinctNotes([
        stripRepeatedLongValueFromNote(savedRecs['dc-power']?.basisNote, [
          savedRecs['dc-power']?.value,
          capValue,
        ]),
        dcPowerDiag.status === 'unresolved' ? dcPowerDiag.unresolvedReason : undefined,
      ]),
    },
    {
      id: 'power',
      label: 'Recommended power & BTM path',
      value: powerValue,
      statusLabel: questionStatusBadgeLabel(
        savedRecs['btm-architecture']
          ? btmArchDiag.status
          : savedRecs['power-type']
            ? powerTypeDiag.status
            : btmArchDiag.status,
        Boolean(savedRecs['btm-architecture']?.value || savedRecs['power-type']?.value || legacyPower?.[0]),
      ),
      notes: distinctNotes([
        stripRepeatedLongValueFromNote(savedRecs['btm-architecture']?.basisNote, [
          savedRecs['btm-architecture']?.value,
          powerValue,
        ]),
        stripRepeatedLongValueFromNote(savedRecs['power-type']?.basisNote, [
          savedRecs['power-type']?.value,
          powerValue,
        ]),
        !savedRecs['btm-architecture'] && !savedRecs['power-type'] ? legacyPower?.[1] : undefined,
      ]),
    },
    {
      id: 'cooling',
      label: 'Recommended cooling & heat rejection',
      value: coolingValue,
      statusLabel: questionStatusBadgeLabel(
        savedRecs['cooling-strategy']
          ? coolingStratDiag.status
          : savedRecs['cooling-type']
            ? coolingTypeDiag.status
            : coolingStratDiag.status,
        Boolean(
          savedRecs['cooling-strategy']?.value ||
            savedRecs['cooling-type']?.value ||
            legacyCooling?.[0],
        ),
      ),
      notes: distinctNotes([
        stripRepeatedLongValueFromNote(savedRecs['cooling-strategy']?.basisNote, [
          savedRecs['cooling-strategy']?.value,
          coolingValue,
        ]),
        stripRepeatedLongValueFromNote(savedRecs['cooling-type']?.basisNote, [
          savedRecs['cooling-type']?.value,
          coolingValue,
        ]),
        !savedRecs['cooling-strategy'] && !savedRecs['cooling-type']
          ? legacyCooling?.[1]
          : undefined,
      ]),
    },
  ];

  const powerRecommendation = recommendationRows[1]!;
  const supportingRecommendations = [recommendationRows[0]!, recommendationRows[2]!];
  const rationaleRows = [powerRecommendation, ...supportingRecommendations].filter(
    row => row.notes.length > 0,
  );

  // Recommendation status summary
  const anyWithheld =
    !isSourceReady ||
    Object.values(savedSet.questions).some(q => q.status === 'withheld-source');
  const allCoreResolved =
    Boolean(savedRecs['dc-power']) &&
    Boolean(savedRecs['btm-architecture'] || savedRecs['power-type']) &&
    Boolean(savedRecs['cooling-strategy'] || savedRecs['cooling-type']);
  const recommendationStatus: AdaptiveProjectIntentOverviewModel['recommendationStatus'] =
    anyWithheld
      ? {
          state: 'withheld',
          status: 'withheld',
          label: 'WITHHELD — QUALITY / SOURCE HOLD',
          explanation:
            savedSet.sourceReason ??
            'One or more saved screening recommendations are withheld because a cited source is unavailable or the report is under a quality hold. Recorded inputs are not promoted to recommendations.',
        }
      : hasSavedRecs && allCoreResolved
        ? {
            state: 'recommended',
            status: 'recommended',
            label: 'RECOMMENDED (ADVISORY — STAGE 2 VERIFICATION REQUIRED)',
            explanation:
              'Saved screening recommendation derived from site evidence. Modeled capacity and supply remain subject to utility and engineering verification.',
          }
        : hasSavedRecs
          ? {
              state: 'conditional',
              status: 'conditional',
              label: 'CONDITIONAL RECOMMENDATION (PARTIAL / EVIDENCE GAPS)',
              explanation:
                'Saved screening recommendation with unresolved dimensions or conditional bounds. Unresolved dimensions are never filled from supplied inputs or inferences.',
            }
          : {
              state: 'unresolved',
              status: 'unresolved',
              label: 'NO SAVED RECOMMENDATION — EVIDENCE GAPS IDENTIFIED',
              explanation:
                savedSet.sourceReason ??
                'No current saved screening recommendation exists in this revision; see material evidence gaps below. Supplied project inputs are kept separate and never treated as a recommendation.',
            };

  // Alternatives list (source-admitted)
  const alternativesList: string[] = [];
  if (isSourceReady) {
    for (const q of Object.values(savedSet.questions)) {
      if (q.status === 'alternatives' && q.alternatives?.length) {
        for (const alt of q.alternatives) {
          if (alt.trim() && !alternativesList.includes(alt.trim())) {
            alternativesList.push(alt.trim());
          }
        }
      }
    }
    for (const row of report.btmDetailedAnalysis ?? []) {
      if (row.viabilityTier === 'alternative' && row.technology?.trim() && row.recommendation?.trim()) {
        const admitted = presentScreeningRecommendationFields(report, [
          `${row.technology.trim()}: ${row.recommendation.trim()}`,
        ]);
        if (admitted?.[0] && !alternativesList.includes(admitted[0])) {
          alternativesList.push(admitted[0]);
        }
      }
    }
  }

  // Material evidence gaps & next steps
  const evidenceGaps: AdaptiveOverviewGapItem[] = [];
  const gapCandidateQuestions: Array<{ id: string; label: string; diag: typeof dcPowerDiag; hasVal: boolean }> = [
    {
      id: 'dc-power',
      label: 'Supportable capacity range',
      diag: dcPowerDiag,
      hasVal: Boolean(savedRecs['dc-power']?.value),
    },
    {
      id: 'btm-architecture',
      label: 'Power & BTM architecture',
      diag: savedRecs['btm-architecture']
        ? btmArchDiag
        : savedRecs['power-type']
          ? powerTypeDiag
          : btmArchDiag,
      hasVal: Boolean(savedRecs['btm-architecture']?.value || savedRecs['power-type']?.value),
    },
    {
      id: 'cooling-strategy',
      label: 'Cooling & heat rejection',
      diag: savedRecs['cooling-strategy']
        ? coolingStratDiag
        : savedRecs['cooling-type']
          ? coolingTypeDiag
          : coolingStratDiag,
      hasVal: Boolean(savedRecs['cooling-strategy']?.value || savedRecs['cooling-type']?.value),
    },
  ];
  const rationaleNoteTexts = new Set(
    rationaleRows.flatMap(r => r.notes.map(n => n.trim()).filter(Boolean)),
  );
  for (const item of gapCandidateQuestions) {
    if (!item.hasVal || item.diag.status === 'unresolved' || item.diag.status === 'withheld-source') {
      const rawGapText =
        item.diag.unresolvedReason ||
        item.diag.basisNote ||
        'No verified recommendation produced in this revision.';
      let gapText = rawGapText;
      if (rawGapText.length > 220 && rationaleNoteTexts.has(rawGapText.trim())) {
        const firstClause = /^(.{40,260}?(?:[.!?](?=\s|$)|;(?=\s)))/.exec(rawGapText.trim());
        if (firstClause?.[1]) {
          const conciseLead = firstClause[1].replace(/;$/, '.');
          gapText = `${conciseLead} (See Saved rationale and conditions above for full site-specific constraints.)`;
        }
      }
      evidenceGaps.push({
        id: item.id,
        label: item.label,
        gap: gapText,
        nextStep: item.diag.nextStep,
      });
    }
  }

  const recordedBriefRows = specRailItems
    .filter(
      row =>
        typeof row.key === 'string' &&
        !['Screening Mode', 'Recommended Load Range', 'Recommended Cooling', 'Recommended Power Path'].includes(
          row.key,
        ),
    )
    .map(row => ({
      ...row,
      label: String(row.key),
    }));

  // Proposal evaluation mode
  const proposalRows =
    screeningMode === 'evaluate-proposal'
      ? buildProposalAlignmentRows(report, effectiveIntent)
      : [];

  const suppliedSummaryParts = recordedBriefRows.map(
    r => `${String(r.key)}: ${String(r.value)}`,
  );
  const proposalSummaryLead =
    suppliedSummaryParts.length > 0
      ? `Declared proposal under evaluation: ${suppliedSummaryParts.join(' · ')}. Modeled capacity and supply figures are screening inputs, not independently confirmed utility or engineering allocations.`
      : 'Declared proposal under evaluation: No fixed capacity, cooling, or schedule targets were supplied in the frozen snapshot; dimensions are assessed against baseline screening evidence.';

  // Offer a clearly labeled alternative only when at least one dimension is conditional/challenged and source-admitted advice exists
  const hasChallengedOrConditional = proposalRows.some(
    r => r.assessmentStatus === 'challenged' || r.assessmentStatus === 'conditional',
  );
  let proposalAlternative: AdaptiveProjectIntentOverviewModel['proposalAlternative'] = null;
  if (screeningMode === 'evaluate-proposal' && hasChallengedOrConditional && isSourceReady) {
    const altParts = distinctNotes([
      savedRecs['dc-power']?.value ? `Capacity: ${savedRecs['dc-power'].value}` : undefined,
      savedRecs['btm-architecture']?.value
        ? `Power path: ${savedRecs['btm-architecture'].value}`
        : savedRecs['power-type']?.value
          ? `Power path: ${savedRecs['power-type'].value}`
          : undefined,
      savedRecs['cooling-strategy']?.value
        ? `Cooling: ${savedRecs['cooling-strategy'].value}`
        : undefined,
      alternativesList[0] ? `BTM alternative: ${alternativesList[0]}` : undefined,
    ]);
    const altNotes = distinctNotes([
      savedRecs['dc-power']?.basisNote,
      savedRecs['btm-architecture']?.basisNote,
      savedRecs['cooling-strategy']?.basisNote,
    ]);
    if (altParts.length > 0) {
      proposalAlternative = {
        label: 'Screening Alternative (Advisory — Not Adopted)',
        summary: altParts.join(' · '),
        rationale: altNotes[0],
      };
    }
  }

  const preservedUserDecisions = (report.preservedUserDecisions ?? [])
    .filter(d => d && d.status === 'applied' && d.parameter && d.value)
    .map(d => `${d.parameter}: ${d.value}`);

  const showModeScorecard =
    effectiveIntent?.screeningMode === 'recommend' ||
    effectiveIntent?.screeningMode === 'evaluate-proposal' ||
    (screeningMode === 'recommend' && hasSavedRecs) ||
    (screeningMode === 'evaluate-proposal' &&
      proposalRows.length > 0 &&
      Boolean(
        (looseReport.scores && looseReport.scores.length > 0) ||
          looseReport.gridAnalysis ||
          looseReport.coolingAnalysis ||
          looseReport.btmViability,
      ));

  const intentDrift = detectProjectIntentDrift(report, reportSite);

  return {
    screeningMode,
    showModeScorecard,
    specRailItems,
    recordedBriefRows,
    recommendationStatus,
    powerRecommendation,
    supportingRecommendations,
    rationaleRows,
    alternativesList,
    evidenceGaps,
    proposalSummaryLead,
    proposalRows,
    proposalAlternative,
    preservedUserDecisions,
    intentDrift,
  };
}
